import React from 'react';
import { createPortal } from 'react-dom';
import ChatAreaHeader from '../../common/chat/shared/ChatAreaHeader';
import ChatHeaderIconButton from '../../common/chat/shared/ChatHeaderIconButton';
import EventFlyerMedia from '../../common/EventFlyerMedia';
import { pushViewState } from './eventChatPageHelpers';

function EventHeaderMenuDot({ tone }) {
  if (!tone || tone === 'none') return null;
  return <span className={`event-header-menu-dot is-${tone}`} aria-hidden="true" />;
}

function EventHeaderMenuBadge({ badge }) {
  const count = Number(badge?.count) || 0;
  if (count <= 0) return null;
  return (
    <span className="event-header-menu-badge">
      <EventHeaderMenuDot tone={badge?.tone} />
      <span>{count}</span>
    </span>
  );
}

export default function EventChatHeader({
  isVisible,
  isNarrow,
  mobileView,
  goToEventList,
  selectedEventFlyerUrl,
  selectedEventFlyerBackUrl,
  selectedEventInfo,
  handleGoHeaderEventDetail,
  onFlyerImageLoad,
  eventHeaderSchedule,
  isSelectedGroupUnified,
  headerNextPerformance,
  isSelectedMultiPerformanceEvent,
  headerNextMultiPerformance,
  headerPerformanceType,
  canViewEventNotes,
  setShowNotes,
  selectedEventId,
  saveLast,
  showNotes,
  noteCount,
  setShowMembersModal,
  participantCounts,
  chatHeaderMenuOpen,
  chatHeaderMenuPosition,
  toggleChatHeaderMenu,
  canViewMaterials,
  materialBadge,
  onOpenMaterials,
  setShowMaterialsModal,
  canViewAlbum,
  canEditAlbum,
  albumBadge,
  onOpenAlbum,
  setShowAlbumModal,
  onOpenLinks,
  onOpenMedia,
  assetMenuBadgeTone,
  closeChatHeaderMenu,
  openSettingsModal,
  canCreateDerivedGroupChat,
  onOpenDerivedGroupChat,
}) {
  if (!isVisible) return null;
  const mobileBackButtonClassName = 'chat-header-back-btn';
  const closeMenuThen = (action) => {
    closeChatHeaderMenu();
    const run = () => action?.();
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(run);
      return;
    }
    setTimeout(run, 0);
  };

  return (
    <ChatAreaHeader
      className="event-chat-header"
      disableMainWrapper
      main={(
        <>
          <div className="event-header-left">
            {isNarrow && mobileView === 'chat' && (
              <ChatHeaderIconButton
                className={mobileBackButtonClassName}
                onClick={goToEventList}
                aria-label="イベント一覧に戻る"
                title="戻る"
                iconClassName="fa-solid fa-chevron-left"
              />
            )}

            <button
              type="button"
              className={`flyer-frame${selectedEventFlyerUrl ? '' : ' is-placeholder'}`}
              onClick={handleGoHeaderEventDetail}
              aria-label="イベント詳細を開く"
              title="イベント詳細を開く"
            >
              {selectedEventFlyerUrl ? (
                <img
                  src={selectedEventFlyerUrl}
                  alt={`${selectedEventInfo.name || 'イベント'}のフライヤー`}
                  className="flyer-image"
                  onLoad={(event) => onFlyerImageLoad(event.currentTarget)}
                />
              ) : (
                <EventFlyerMedia
                  event={selectedEventInfo}
                  imageUrl=""
                  sizeVariant="small"
                  borderRadius={8}
                  hasImage={Boolean(selectedEventFlyerUrl || selectedEventFlyerBackUrl)}
                />
              )}
            </button>
          </div>

          <div className="event-header-main">
            <div className="event-meta-top">
              <i className="fa-solid fa-calendar-days" aria-hidden="true"></i>
              <span className="event-date__value">{eventHeaderSchedule.date || '未設定'}</span>
              {eventHeaderSchedule.time && (
                <span className="event-date__time">{eventHeaderSchedule.time}</span>
              )}
            </div>

            <div className="event-title-stack">
              <button
                type="button"
                className="event-title-link"
                onClick={handleGoHeaderEventDetail}
                title={isSelectedGroupUnified ? '次の公演のイベント詳細を開く' : 'イベント詳細を開く'}
              >
                {selectedEventInfo.name || 'イベント名未設定'}
              </button>
              {isSelectedGroupUnified && headerNextPerformance?.label ? (
                <div className="event-next-line">{headerNextPerformance.label}</div>
              ) : null}
              {isSelectedMultiPerformanceEvent && headerNextMultiPerformance?.label ? (
                <div className="event-next-line">{headerNextMultiPerformance.label}</div>
              ) : null}
              {!isSelectedGroupUnified && !isSelectedMultiPerformanceEvent && headerPerformanceType && (
                <div className="event-performance-line">
                  {headerPerformanceType}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      actionsClassName="event-header-actions chat-header-action-group"
      actions={(
        <>
          {canViewEventNotes ? (
            <ChatHeaderIconButton
              className="member-icon"
              onClick={() => {
                setShowNotes((current) => {
                  const next = !current;
                  pushViewState(selectedEventId, next ? 'notes-list' : 'chat');
                  saveLast(selectedEventId, next ? 'notes-list' : 'chat');
                  return next;
                });
              }}
              title={showNotes ? 'チャットに戻る' : 'ノート'}
              aria-label={showNotes ? 'チャットに戻る' : 'ノート'}
              iconClassName={`fa-solid ${showNotes ? 'fa-comment' : 'fa-note-sticky'}`}
              badgeCount={showNotes ? 0 : noteCount}
            />
          ) : null}

          <ChatHeaderIconButton
            className="member-icon"
            onClick={() => setShowMembersModal(true)}
            title="メンバーリスト"
            aria-label="メンバーリスト"
            iconClassName="fa-solid fa-people-group"
            badgeCount={participantCounts.total}
            showZeroBadge
          />

          <div className="event-header-menu-wrap">
            <button
              type="button"
              className={`member-icon${assetMenuBadgeTone && assetMenuBadgeTone !== 'none' ? ' has-event-assets' : ''}`}
              onClick={toggleChatHeaderMenu}
              title="メニュー"
              aria-label="メニュー"
            >
              <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true"></i>
              <EventHeaderMenuDot tone={assetMenuBadgeTone} />
            </button>
            {chatHeaderMenuOpen && chatHeaderMenuPosition && typeof document !== 'undefined' ? createPortal(
              <div
                className="event-header-menu event-header-menu--floating ds-compact-action-menu"
                role="menu"
                style={{
                  left: `${chatHeaderMenuPosition.left}px`,
                  top: `${chatHeaderMenuPosition.top}px`,
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canViewMaterials}
                  onClick={() => {
                    closeMenuThen(() => {
                      if (onOpenMaterials) onOpenMaterials();
                      else setShowMaterialsModal(true);
                    });
                  }}
                >
                  <i className="fa-solid fa-folder-open" aria-hidden="true"></i>
                  <span>イベント資料</span>
                  <EventHeaderMenuBadge badge={materialBadge} />
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canViewAlbum && !canEditAlbum}
                  onClick={() => {
                    closeMenuThen(() => {
                      if (onOpenAlbum) onOpenAlbum();
                      else setShowAlbumModal(true);
                    });
                  }}
                >
                  <i className="fa-solid fa-images" aria-hidden="true"></i>
                  <span>イベントアルバム</span>
                  <EventHeaderMenuBadge badge={albumBadge} />
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeMenuThen(onOpenLinks);
                  }}
                >
                  <i className="fa-solid fa-link" aria-hidden="true"></i>
                  <span>リンク</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeMenuThen(onOpenMedia);
                  }}
                >
                  <i className="fa-solid fa-photo-film" aria-hidden="true"></i>
                  <span>写真・ファイル</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canCreateDerivedGroupChat}
                  onClick={() => {
                    closeMenuThen(onOpenDerivedGroupChat);
                  }}
                >
                  <i className="fa-solid fa-user-group" aria-hidden="true"></i>
                  <span>グループ作成</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    closeMenuThen(() => openSettingsModal(selectedEventInfo, { selectOnOpen: true }));
                  }}
                >
                  <i className="fa-solid fa-gear" aria-hidden="true"></i>
                  <span>通知設定</span>
                </button>
              </div>
            , document.body) : null}
          </div>
        </>
      )}
    />
  );
}
